import { lucideIcons, esc } from '../app.js';

const SECTIONS = [
  {
    title: 'Overview & Threat Model',
    icon: 'shield',
    content: `
      <p>BlindKeep is a <strong>zero-knowledge</strong> encrypted vault. All encryption and decryption happen exclusively in your browser (via WebAssembly) or mobile app. The server never has access to your plaintext data, decryption keys, or any material to derive them.</p>
      <p><strong>Threat model:</strong> We assume the server is an <em>honest-but-curious adversary</em> — it faithfully executes the protocol but may inspect all stored data. Even with full database and object storage access, an attacker learns <strong>nothing</strong> about your plaintext content.</p>
      <p><strong>What the server can see</strong> (inherent to any server-mediated system):</p>
      <ul>
        <li>Email addresses (needed for account recovery and will notifications)</li>
        <li>File sizes in bytes (needed for billing) — new uploads are padded to size buckets to reduce granularity</li>
        <li>Timestamps on database rows</li>
        <li>Request IP addresses (rate limiting and audit logs)</li>
        <li>Number of items, grants, and drops per user</li>
      </ul>
    `
  },
  {
    title: 'Key Hierarchy',
    icon: 'key-round',
    content: `
      <p>Your password never leaves your device. Instead, it's stretched into a high-entropy key, which is then split into two cryptographically independent branches:</p>
      <pre class="arch-diagram">Password
    │
    ▼
Argon2id(password, client_salt)  →  password_key (256-bit)
  [64 MiB memory, 3 iterations]
    │
    ├── HKDF("vault-auth")  →  auth_key
    │       └── Sent to server, hashed again with Argon2id
    │           Server stores only: auth_key_hash
    │
    ├── HKDF("vault-enc")   →  key_wrapping_key
    │       └── Wraps/unwraps master_key (never sent to server)
    │
    └── (password_key is discarded)

master_key (random 256-bit, created at registration)
    │
    ├── HKDF("vault-enc")   →  enc_key
    │       ├── Encrypts per-item keys
    │       └── Encrypts your X25519 private key
    │
    └── HKDF("vault-wrap")  →  wrap_key (reserved)</pre>
      <p><strong>Key properties:</strong></p>
      <ul>
        <li>The <strong>auth path</strong> ("vault-auth") is completely separated from the <strong>encryption path</strong> ("vault-enc") via HKDF domain separation — knowing one reveals nothing about the other</li>
        <li>The <code>master_key</code> is a random value, never derived from your password directly</li>
        <li>The <code>password_key</code> is an intermediate value, discarded immediately after deriving subkeys</li>
      </ul>
    `
  },
  {
    title: 'Items (Notes & Files)',
    icon: 'box',
    content: `
      <p>Every item in your vault is encrypted with its own unique random key — a design called <strong>per-item key isolation</strong>.</p>
      <p><strong>Encryption chain (V1 format):</strong></p>
      <ol class="arch-steps">
        <li>A random 256-bit <code>item_key</code> is generated</li>
        <li>Your plaintext is encrypted: <code>XChaCha20-Poly1305(item_key, plaintext, aad="item:{userId}")</code></li>
        <li>The <code>item_key</code> itself is wrapped: <code>XChaCha20-Poly1305(enc_key, item_key, aad="wrap:{userId}")</code></li>
        <li>The encrypted blob goes to S3, the wrapped key goes to the database</li>
      </ol>
      <p>The <strong>Associated Authenticated Data (AAD)</strong> binds each ciphertext to its owner and context — even with database write access, an attacker cannot swap encrypted data between users or contexts without causing decryption failure.</p>
      <p>The server stores only ciphertext. To decrypt, you need <code>item_key</code> → which needs <code>enc_key</code> → which needs <code>master_key</code> → which needs your password. The server has none of these.</p>
      <p><strong>Blob size padding:</strong> New uploads are padded to size buckets before encryption, reducing the granularity of the file-size side channel:</p>
      <table class="arch-table">
        <thead><tr><th>Actual size</th><th>Padded to</th></tr></thead>
        <tbody>
          <tr><td>0 – 1 KiB</td><td>1 KiB</td></tr>
          <tr><td>1 – 16 KiB</td><td>Next power of 2</td></tr>
          <tr><td>16 KiB – 1 MiB</td><td>Next 64 KiB boundary</td></tr>
          <tr><td>1 – 64 MiB</td><td>Next 1 MiB boundary</td></tr>
          <tr><td>64 MiB+</td><td>Next 8 MiB boundary</td></tr>
        </tbody>
      </table>
      <p><strong>Server-side enforcement</strong> (defense-in-depth): <code>item_type</code> must be <code>"encrypted"</code> and <code>metadata</code> must be empty — the server rejects any plaintext metadata.</p>
    `
  },
  {
    title: 'Sharing (Grants)',
    icon: 'share-2',
    content: `
      <p>You can share vault items with others using two methods, both zero-knowledge:</p>
      <h4>X25519 Key Exchange (registered users)</h4>
      <ol class="arch-steps">
        <li>You unwrap the <code>item_key</code> using your <code>enc_key</code></li>
        <li>A fresh ephemeral X25519 keypair is generated</li>
        <li>A shared secret is computed: <code>X25519(ephemeral_private, recipient_public)</code></li>
        <li>A key-bound wrapping key is derived: <code>HKDF(shared_secret, salt=eph_pub||recipient_pub, "vault-grant-wrap")</code></li>
        <li>The <code>item_key</code> is re-encrypted for the recipient with AAD binding</li>
      </ol>
      <p>The ephemeral private key is discarded immediately, providing <strong>sender forward secrecy</strong> — compromise of your long-term keys won't expose previously shared grants. Key-bound HKDF prevents misdirection attacks by binding the wrapping key to both parties' public keys.</p>
      <h4>Link-Secret (anyone, no account needed)</h4>
      <p>A random 32-byte <code>link_secret</code> is embedded in the URL fragment (<code>#</code>). URL fragments are <strong>never sent to the server</strong> per the HTTP specification. The server stores only the ciphertext.</p>
      <p><strong>Policy controls:</strong> You can set view limits, expiry dates, IP restrictions, and revoke access at any time.</p>
    `
  },
  {
    title: 'Digital Will',
    icon: 'scroll-text',
    content: `
      <p>The digital will lets you designate an heir to receive selected vault items if you become unreachable.</p>
      <ol class="arch-steps">
        <li>You generate a random <code>will_key</code></li>
        <li>Each selected item's <code>item_key</code> is unwrapped and re-wrapped with <code>will_key</code></li>
        <li>The <code>will_key</code> is encrypted for your heir using either:
          <ul>
            <li><strong>X25519:</strong> if your heir has an account</li>
            <li><strong>BIP39 mnemonic:</strong> a 12-word passphrase you communicate out-of-band (printed, written down)</li>
          </ul>
        </li>
      </ol>
      <p><strong>Dead man's switch:</strong> BlindKeep checks in with you on a schedule you set (weekly, monthly, quarterly). Miss enough check-ins and the will activates, emailing your heir a secure access link.</p>
      <p>The server cannot decrypt the will — it requires either the heir's private key or the BIP39 mnemonic, neither of which the server possesses.</p>
    `
  },
  {
    title: 'Drops (Anonymous Encrypted Sharing)',
    icon: 'upload-cloud',
    content: `
      <p>Drops are anonymous, encrypted file shares — no account needed. Files are encrypted in your browser and auto-delete after 60 minutes.</p>
      <p><strong>Two access methods:</strong></p>
      <ul>
        <li><strong>Direct link:</strong> The decryption key is in the URL fragment (never sent to the server)</li>
        <li><strong>BIP39 mnemonic (12 words):</strong> The key is derived from a 12-word passphrase using PBKDF2. You share the words separately from the pickup link for security.</li>
      </ul>
      <p>The server stores only the encrypted blob and a lookup key (derived via HKDF, cannot be reversed to the decryption key). It has no way to decrypt the file.</p>
    `
  },
  {
    title: 'Passkeys (WebAuthn + PRF)',
    icon: 'fingerprint',
    content: `
      <p>Passkeys provide passwordless authentication while maintaining zero-knowledge encryption using the <strong>WebAuthn PRF extension</strong>:</p>
      <ol class="arch-steps">
        <li>Your hardware authenticator computes: <code>prf_output = PRF(authenticator_secret, prf_salt)</code></li>
        <li>An encryption key is derived: <code>HKDF(prf_output, "vault-passkey-v1")</code></li>
        <li>Your <code>master_key</code> is wrapped with this key and stored on the server</li>
      </ol>
      <p>The <code>prf_output</code> is produced by the hardware authenticator and <strong>never leaves the client</strong>. The server stores only <code>prf_salt</code> (an input) and the wrapped master key — it cannot compute the PRF output without the authenticator's internal secret.</p>
    `
  },
  {
    title: 'Recovery',
    icon: 'life-buoy',
    content: `
      <p>During setup, you can generate a <strong>12-word recovery phrase</strong> (BIP39 mnemonic). This serves as a backup if you forget your password.</p>
      <ol class="arch-steps">
        <li>A seed is derived: <code>PBKDF2-SHA512(mnemonic, "vault-recovery", 600,000 iterations)</code></li>
        <li>The first 32 bytes become the <code>recovery_wrapping_key</code></li>
        <li>A separate <code>recovery_auth_key</code> is derived via HKDF for authentication</li>
        <li>The server stores: <code>Argon2id(recovery_auth_key)</code> and <code>XChaCha20(recovery_wrapping_key, master_key)</code></li>
      </ol>
      <p>The mnemonic is generated client-side and communicated out-of-band (written down, printed). The server cannot reverse Argon2id to get the auth key, nor derive the wrapping key from it.</p>
      <p>Recovery keys are versioned — new setups use 600,000 PBKDF2 iterations for defense-in-depth. Legacy recovery keys (with lower iteration counts) continue to work, and are automatically upgraded when regenerated.</p>
    `
  },
  {
    title: 'Algorithms',
    icon: 'cpu',
    content: `
      <table class="arch-table">
        <thead><tr><th>Algorithm</th><th>Parameters</th><th>Use</th><th>Rationale</th></tr></thead>
        <tbody>
          <tr>
            <td><strong>XChaCha20-Poly1305</strong></td>
            <td>256-bit key, 192-bit nonce</td>
            <td>All symmetric encryption</td>
            <td>24-byte nonce eliminates reuse risk. AEAD provides authenticity. V1 format adds AAD context binding.</td>
          </tr>
          <tr>
            <td><strong>X25519</strong></td>
            <td>Curve25519, 32-byte keys</td>
            <td>Key agreement for grants and wills</td>
            <td>Fast, constant-time, resistant to timing attacks. V1 adds key-bound HKDF salt.</td>
          </tr>
          <tr>
            <td><strong>Argon2id</strong></td>
            <td>64 MiB, t=3, p=1</td>
            <td>Password stretching, auth key hashing</td>
            <td>Memory-hard, resists GPU/ASIC attacks. Exceeds OWASP minimum.</td>
          </tr>
          <tr>
            <td><strong>HKDF-SHA256</strong></td>
            <td>Domain-separated info, optional salt</td>
            <td>Subkey derivation</td>
            <td>Proven PRF-based extract-and-expand. V1 grant wrapping uses key-bound salt for misdirection resistance.</td>
          </tr>
          <tr>
            <td><strong>PBKDF2-SHA512</strong></td>
            <td>600,000 iterations (current)</td>
            <td>BIP39 mnemonic key derivation</td>
            <td>Hardened iteration count for defense-in-depth. Legacy keys at 2,048 iterations remain supported.</td>
          </tr>
        </tbody>
      </table>
    `
  },
  {
    title: 'Security Properties',
    icon: 'lock',
    content: `
      <ul>
        <li><strong>AAD context binding:</strong> All new encryption uses V1 format with Associated Authenticated Data, binding ciphertext to its owner and context. Prevents cross-user or cross-context ciphertext substitution attacks.</li>
        <li><strong>Key-bound grant wrapping:</strong> V1 X25519 grants bind the HKDF derivation to both public keys (<code>eph_pub || recipient_pub</code>), preventing key misdirection attacks.</li>
        <li><strong>Key isolation:</strong> HKDF domain separation (<code>"vault-auth"</code>, <code>"vault-enc"</code>, <code>"vault-grant-wrap"</code>, etc.) ensures subkeys from the same parent are cryptographically independent. Compromise of one domain reveals nothing about another.</li>
        <li><strong>Per-item key isolation:</strong> Every item, grant, drop, and will uses a unique random 256-bit key. Compromise of one item key reveals nothing about others.</li>
        <li><strong>Sender forward secrecy:</strong> Each grant uses a fresh ephemeral X25519 keypair, discarded after use. Compromise of long-term keys doesn't expose previously shared grants.</li>
        <li><strong>Nonce safety:</strong> XChaCha20-Poly1305 uses 24-byte random nonces. Collision probability is ~2<sup>-96</sup> after 2<sup>48</sup> encryptions — negligible.</li>
        <li><strong>Key zeroization:</strong> Rust uses <code>#[zeroize(drop)]</code> to overwrite key material in memory. The browser client fills key buffers with zeros on lock/logout and on page unload.</li>
        <li><strong>Anti-enumeration:</strong> Every public endpoint returns plausible fake data for non-existent accounts — fake salts, fake recovery blobs, fake WebAuthn challenges. You cannot tell whether an email is registered.</li>
        <li><strong>Rate limiting:</strong> Auth endpoints: 5 req/IP/60s. Magic links: 1/email/2min. Drops: 10/IP/hour. IP extraction uses ConnectInfo with trusted-proxy support.</li>
        <li><strong>No plaintext leakage:</strong> The server enforces that <code>item_type</code> is <code>"encrypted"</code> and <code>metadata</code> is empty — rejecting any plaintext data.</li>
        <li><strong>Key rotation:</strong> Password changes atomically re-wrap all item keys, the encrypted private key, recovery key, and passkey PRF wrappings in a single database transaction.</li>
        <li><strong>JWT algorithm pinning:</strong> Token validation is pinned to HS256, preventing algorithm confusion attacks.</li>
        <li><strong>XSS prevention:</strong> All dynamic content is HTML-escaped. External dependencies (Lucide icons) are bundled locally with SRI hash verification.</li>
      </ul>
    `
  },
];

export function renderArchitecture(container) {
  container.innerHTML = `
    <div class="landing-header">
      <a class="landing-logo" href="#/drop" style="text-decoration:none;color:var(--text)">
        <svg viewBox="0 0 32 32" width="36" height="36" style="flex-shrink:0"><defs><linearGradient id="bk-arch" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-arch)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-arch)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-arch)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </a>
      <div style="display:flex;align-items:center;gap:16px">
        <a href="#/faq" style="font-size:14px;font-weight:500;color:var(--text-secondary);text-decoration:none;transition:color .15s" onmouseover="this.style.color='var(--accent)'" onmouseout="this.style.color='var(--text-secondary)'">FAQ</a>
        <a class="landing-signin" href="#/login">Sign in</a>
      </div>
    </div>
    <div class="arch-body">
      <h1 class="arch-title">Cryptographic Architecture</h1>
      <p class="arch-subtitle">How BlindKeep achieves zero-knowledge encryption — a technical reference for users and auditors.</p>
      <div class="arch-list">
        ${SECTIONS.map((s, i) => `
          <details class="faq-item arch-item" ${i === 0 ? 'open' : ''}>
            <summary class="faq-question">
              <span style="display:flex;align-items:center;gap:10px">
                <i data-lucide="${s.icon}" style="width:18px;height:18px;flex-shrink:0;color:var(--accent);opacity:.7"></i>
                ${esc(s.title)}
              </span>
              <i data-lucide="chevron-down" class="faq-chevron" style="width:18px;height:18px;flex-shrink:0"></i>
            </summary>
            <div class="faq-answer arch-answer">${s.content}</div>
          </details>
        `).join('')}
      </div>
    </div>
    <div class="landing-footer"><a href="#/architecture" class="arch-footer-link">End-to-end encrypted</a> &middot; <a href="#/architecture" class="arch-footer-link">Zero knowledge</a> &middot; <a href="#/privacy" class="arch-footer-link">No tracking</a> &middot; <a href="#/compare" class="arch-footer-link">Compare</a> &middot; <a href="#/faq" class="arch-footer-link">FAQ</a></div>
    <div class="eu-badge">\u{1F1EA}\u{1F1FA} Made &amp; operated in the EU</div>
  `;
  lucideIcons();
}
